import config from './index';

const baseUrl = () => `${config.apiUrl}/api/v1/admin/order-flow`;

function headers() {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function parseBody(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

export function getErrorMessage(body, fallback = 'Request failed') {
  if (!body || typeof body !== 'object') return fallback;
  return body.message || body.error || fallback;
}

const ADMIN_IMAGE_UPLOAD_URL =
  'https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload';

// Mirrors ALLOWED_MIME_TYPES in the Supabase image-upload edge function (images + PDF subset).
const IMAGE_DIRECT_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const UPLOAD_MAX_FILE_SIZE = 10 * 1024 * 1024; // matches the edge function's 10MB limit

/**
 * Convert an image the edge function would reject (e.g. iPhone HEIC) to JPEG.
 * Uses canvas decode, so it works wherever the browser itself can display the format.
 */
async function convertImageFileToJpeg(file) {
  const imageCompression = (await import('browser-image-compression')).default;
  return imageCompression(file, {
    fileType: 'image/jpeg',
    maxSizeMB: 8,
    maxWidthOrHeight: 2560,
    useWebWorker: true,
    initialQuality: 0.9,
  });
}

function looksLikeImageFile(file) {
  const type = String(file?.type || '').toLowerCase();
  if (type.startsWith('image/')) return true;
  // Some OS/camera pickers leave MIME empty; allow common image extensions through.
  const name = String(file?.name || '').toLowerCase();
  return /\.(jpe?g|png|webp|gif|heic|heif|bmp|tiff?)$/i.test(name);
}

async function prepareImageForEdgeUpload(file) {
  const type = String(file?.type || '').toLowerCase();
  let uploadFile = file;
  if (!IMAGE_DIRECT_UPLOAD_TYPES.includes(type)) {
    // e.g. image/heic, image/gif, or empty MIME — server only accepts PNG/JPEG/WebP.
    try {
      uploadFile = await convertImageFileToJpeg(file);
    } catch {
      throw new Error(
        'This image format is not supported. Please choose a JPG or PNG photo.'
      );
    }
  }
  if (uploadFile.size > UPLOAD_MAX_FILE_SIZE) {
    throw new Error('Image is too large (max 10 MB)');
  }
  return uploadFile;
}

async function postFileToImageUpload(uploadFile, failureLabel) {
  const token = localStorage.getItem('token');
  const form = new FormData();
  form.append('file', uploadFile);
  const res = await fetch(ADMIN_IMAGE_UPLOAD_URL, {
    method: 'POST',
    headers: { 'X-Client': 'ADMIN', Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    let serverMsg = '';
    try {
      const parsed = JSON.parse(errText);
      serverMsg = parsed?.message || parsed?.error || '';
    } catch {
      serverMsg = errText;
    }
    throw new Error(
      serverMsg ? `${failureLabel}: ${serverMsg}` : `${failureLabel} (${res.status})`
    );
  }
  const data = await res.json();
  const url = Array.isArray(data) ? data[0] : data?.url;
  if (!url || !String(url).toLowerCase().startsWith('https://')) {
    throw new Error(`${failureLabel} did not return a valid HTTPS URL`);
  }
  return url;
}

/** Upload one image for admin flows (order create, quotations, etc.) — returns public URL. */
export async function uploadAdminImageFile(file) {
  if (!file || !looksLikeImageFile(file)) {
    throw new Error('Please choose an image file');
  }
  const uploadFile = await prepareImageForEdgeUpload(file);
  return postFileToImageUpload(uploadFile, 'Upload failed');
}

/** Upload an offline-payment proof (image or PDF) and return its public HTTPS URL. */
export async function uploadAdminPaymentProofFile(file) {
  const type = String(file?.type || '').toLowerCase();
  if (!file || (!looksLikeImageFile(file) && type !== 'application/pdf')) {
    throw new Error('Payment proof must be an image or PDF');
  }

  let uploadFile = file;
  if (type === 'application/pdf') {
    if (uploadFile.size > UPLOAD_MAX_FILE_SIZE) {
      throw new Error('Proof file is too large (max 10 MB)');
    }
  } else {
    uploadFile = await prepareImageForEdgeUpload(file);
  }

  return postFileToImageUpload(uploadFile, 'Proof upload failed');
}

async function request(path, options = {}) {
  const res = await fetch(`${baseUrl()}${path}`, {
    ...options,
    headers: { ...headers(), ...options.headers },
  });
  const body = await parseBody(res);
  return { ok: res.ok, status: res.status, body };
}

export function getUsersByMobile(mobile) {
  const q = encodeURIComponent(mobile || '');
  return request(`/users-by-mobile?mobile=${q}`);
}

export function createOrderFlowUser(payload) {
  return request('/users', { method: 'POST', body: JSON.stringify(payload) });
}

export function getUserAddresses(userId) {
  return request(`/users/${encodeURIComponent(userId)}/addresses`);
}

export function createUserAddress(userId, payload, { skipServiceabilityCheck = false } = {}) {
  const qs = skipServiceabilityCheck ? '?skip_serviceability_check=true' : '';
  return request(`/users/${encodeURIComponent(userId)}/addresses${qs}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function createOrderForUser(payload) {
  return request('/orders', { method: 'POST', body: JSON.stringify(payload) });
}

export function getNotificationTemplates() {
  return request('/notification-templates');
}

export function postSendOrderNotification(payload) {
  return request('/notifications/send', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getChargeTypes() {
  return request('/charge-types');
}

/** All Prisma CHARGE_TYPE values for admin extra-charge UI (not limited to service.valid_extra_charge). */
export async function fetchChargeTypesForOrderFlow() {
  const { ok, body } = await getChargeTypes();
  if (!ok) {
    throw new Error(getErrorMessage(body, 'Failed to load charge types'));
  }
  const list = body?.data?.charge_types;
  return Array.isArray(list) ? list : [];
}

export function postQuotation(orderId, payload) {
  return request(`/orders/${orderId}/quotation`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function postExtraCharge(orderId, payload) {
  return request(`/orders/${orderId}/extra-charge`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Planned paid-job visit time (persisted without marking on the way).
 * `PATCH /api/v1/admin/order-flow/orders/:orderId/planned-job-visit`
 * Body: `{ scheduled_visit_at?: string | null }` — use `null` to clear.
 */
export function patchPlannedJobVisit(orderId, payload = {}) {
  const body = payload != null && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  return request(`/orders/${orderId}/planned-job-visit`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Turn 2do Guarantee Fee on or off for an order.
 * `PATCH /api/v1/admin/order-flow/orders/:orderId/apply-guarantee-fee`
 * Body: `{ apply_guarantee_fee: boolean }` only (server handles PaymentIntent sync).
 */
export function patchApplyGuaranteeFee(orderId, payload = {}) {
  const raw = payload != null && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  return request(`/orders/${orderId}/apply-guarantee-fee`, {
    method: 'PATCH',
    body: JSON.stringify({ apply_guarantee_fee: Boolean(raw.apply_guarantee_fee) }),
  });
}

/**
 * Optional body: `{ eta_minutes?: number, scheduled_visit_at?: string }`.
 * Extra fields are safe to omit; servers may record `scheduled_visit_at` when supported.
 */
export function postOnTheWay(orderId, payload = {}) {
  const body = payload != null && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  return request(`/orders/${orderId}/on-the-way`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Optional body includes `cancel_pending_inspections`, `scheduled_visit_at`, etc., per server support. */
export function postStartJob(orderId, payload = {}) {
  const body = payload != null && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  return request(`/orders/${orderId}/start-job`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function postEndJob(orderId, payload = {}) {
  return request(`/orders/${orderId}/end-job`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Prefer `worker_id` from contractor workers API; alternatively send `{ user_id: worker-user-id }`.
 */
export function postAssignWorker(orderId, payload) {
  const body =
    typeof payload === 'string'
      ? { worker_id: payload }
      : payload != null && typeof payload === 'object'
        ? payload
        : {};
  return request(`/orders/${orderId}/assign-worker`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Eligible contractors for this order (service + area). Same route as Order Details sidebar. */
export async function fetchOrderEligibleContractors(orderId) {
  const token = localStorage.getItem('token');
  const res = await fetch(
    `${config.apiUrl}/api/v1/admin/orders/${encodeURIComponent(orderId)}/contractors`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const msg =
      data && typeof data === 'object' && typeof data.message === 'string'
        ? data.message
        : 'Failed to load contractors';
    throw new Error(msg);
  }
  return Array.isArray(data) ? data : [];
}

export async function postAssignContractorToOrder(orderId, contractorId) {
  const token = localStorage.getItem('token');
  const res = await fetch(
    `${config.apiUrl}/api/v1/admin/orders/${encodeURIComponent(orderId)}/assign-contractor`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ contractor_id: contractorId }),
    }
  );
  const body = await parseBody(res);
  return { ok: res.ok, status: res.status, body };
}

export function patchOrderFinancials(orderId, payload) {
  return request(`/orders/${orderId}/financials`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/** Master payload: invoice breakdown, charges, completion_options, payment comparison, contractor preview, etc. */
export function getOrderFinancialsMaster(orderId) {
  return request(`/orders/${orderId}/financials-master`);
}

/**
 * Full payments + refunds ledger for Payment information UI.
 * All payment_details, nested refunds, summary, payment link, wallet rows.
 */
export function getOrderPaymentsLedger(orderId) {
  return request(`/orders/${orderId}/payments-ledger`);
}

/** Active contractor/worker assigned to the order plus all active admins. */
export function getOfflinePaymentCollectors(orderId) {
  return request(`/orders/${encodeURIComponent(orderId)}/payments/offline/collectors`);
}

/** Record a successful offline payment against the current order invoice. */
export function postOfflinePayment(orderId, payload) {
  return request(`/orders/${encodeURIComponent(orderId)}/payments/offline`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Inspection sessions on the order — session-based inspection flow */
export function getOrderInspectionSessions(orderId) {
  return request(`/orders/${orderId}/inspections`);
}

/** Job sessions (type JOB vs INSPECTION) */
export function getOrderJobSessionsList(orderId) {
  return request(`/orders/${orderId}/sessions`);
}

export function getInspectionTimeOptions(orderId, inspectionSessionId) {
  const sid = encodeURIComponent(inspectionSessionId);
  return request(`/orders/${orderId}/inspection/${sid}/time-options`);
}

export function postInspectionSelectSlot(orderId, inspectionSessionId, payload) {
  const sid = encodeURIComponent(inspectionSessionId);
  return request(`/orders/${orderId}/inspection/${sid}/select-slot`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function postInspectionSchedule(orderId, payload) {
  return request(`/orders/${orderId}/inspection/schedule`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function patchInspectionReschedule(orderId, inspectionSessionId, payload) {
  const sid = encodeURIComponent(inspectionSessionId);
  return request(`/orders/${orderId}/inspection/${sid}/reschedule`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function postInspectionStart(orderId, payload = {}) {
  return request(`/orders/${orderId}/inspection/start`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function postInspectionComplete(orderId, inspectionSessionId, payload) {
  const sid = encodeURIComponent(inspectionSessionId);
  return request(`/orders/${orderId}/inspection/${sid}/complete`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Optional body: `{ start_photos?: string[] }` */
export function postInspectionStartJob(orderId, inspectionSessionId, payload = {}) {
  const sid = encodeURIComponent(inspectionSessionId);
  const body = payload != null && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  return request(`/orders/${orderId}/inspection/${sid}/start-job`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Visit purpose + next visit type enums for inspection UI (contractor parity). */
export function getInspectionOptions() {
  return request('/inspection/options');
}

/** Job session segment control (admin parity with contractor/worker session routes). */
export function postJobSessionStart(orderId, payload = {}) {
  const body = payload != null && typeof payload === 'object' ? payload : {};
  return request(`/orders/${orderId}/job-session/start`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function postJobSessionPause(orderId, payload) {
  return request(`/orders/${orderId}/job-session/pause`, {
    method: 'POST',
    body: JSON.stringify(payload != null && typeof payload === 'object' ? payload : {}),
  });
}

export function postJobSessionResume(orderId, payload = {}) {
  const body = payload != null && typeof payload === 'object' ? payload : {};
  return request(`/orders/${orderId}/job-session/resume`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function postJobSessionComplete(orderId, payload) {
  return request(`/orders/${orderId}/job-session/complete`, {
    method: 'POST',
    body: JSON.stringify(payload != null && typeof payload === 'object' ? payload : {}),
  });
}

export function patchOrderFlowCharge(orderId, chargeId, payload) {
  return request(`/orders/${orderId}/charges/${encodeURIComponent(chargeId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/** Accept PENDING / ADMIN_APPROVED charge on behalf of the customer (not for COMPLETE/CANCELED orders). */
export function postAcceptOrderFlowCharge(orderId, chargeId, payload = {}) {
  return request(`/orders/${orderId}/charges/${encodeURIComponent(chargeId)}/accept`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Download customer invoice PDF (admin JWT). */
export async function downloadOrderInvoicePdf(orderId, { filename } = {}) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${baseUrl()}/orders/${encodeURIComponent(orderId)}/invoice.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(errText || `PDF failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `invoice-order-${orderId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function postSyncIntentFromOrder(orderId) {
  return request(`/orders/${orderId}/payment/sync-intent-from-order`, {
    method: 'POST',
  });
}

export function postSetIntentAmount(orderId, amountCents) {
  return request(`/orders/${orderId}/payment/set-intent-amount`, {
    method: 'POST',
    body: JSON.stringify({ amount_cents: Number(amountCents) }),
  });
}

export function postAttachPaymentMethod(orderId, paymentMethodId) {
  return request(`/orders/${orderId}/payment/attach-method`, {
    method: 'POST',
    body: JSON.stringify({ payment_method_id: paymentMethodId }),
  });
}

export function postCompleteOrder(orderId, payload) {
  return request(`/orders/${orderId}/complete`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Create or reuse a stable customer pay link for the order (no body). */
export function postOrderPaymentLink(orderId) {
  return request(`/orders/${orderId}/payment-link`, { method: 'POST' });
}

/**
 * Queue payment link SMS (worker creates link + sends SMS).
 * Body: `{ order_id: number }` — `orderId` path param also accepted by server.
 */
export function postPaymentLinkSend(orderId) {
  const id = Number(orderId);
  return request('/payment-link/send', {
    method: 'POST',
    body: JSON.stringify({ order_id: Number.isFinite(id) ? id : orderId }),
  });
}

/**
 * Refund a captured Stripe PaymentIntent (`pi_*`).
 * Body: `{ amount_cents?: number, reason?: 'duplicate' | 'fraudulent' | 'requested_by_customer' }`
 */
export function postPaymentRefund(paymentId, payload = {}) {
  const raw = payload != null && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  const body = {};
  if (raw.amount_cents != null && raw.amount_cents !== '') {
    body.amount_cents = Number(raw.amount_cents);
  }
  if (raw.reason) body.reason = raw.reason;
  return request(`/payments/${encodeURIComponent(paymentId)}/refund`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function fetchServicesForOrderFlow() {
  const token = localStorage.getItem('token');
  const res = await fetch(
    `${config.apiUrl}/api/v1/admin/service?disable_pagination=true`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const data = await res.json().catch(() => []);
  if (!res.ok) throw new Error('Failed to load services');
  return Array.isArray(data) ? data : [];
}

export async function fetchCombosForOrderFlow() {
  const token = localStorage.getItem('token');
  const res = await fetch(`${config.apiUrl}/api/v1/admin/combo`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(result.message || 'Failed to load combos');
  return Array.isArray(result.data) ? result.data : [];
}
